import { useState, useCallback } from 'react';
import { IWatrTrip } from '@nkr/models/watr-trip';
import { ILineItem } from '../types/line-item.types';
import { IOverrideState } from '../types/verify-travel.types';
import { calculateExpenseTotals, sumMileageLineItemAmount, sumMileageRoundTripMiles, deriveMileageRateFromLineItems } from '../utils/expense.utils';
import { resolveMileageRate } from '../../year-rates';
import { IWatrLimit } from '@nkr/models/index';
import {
    calculateApprovedLodgingAmount,
    calculateApprovedTransportationAmount,
    calculateApprovedMealsAmount,
    calculateApprovedDependentCareAmount,
    calculateApprovedMileageAmount,
    calculateApprovedFlightAmount,
    calculateRawRequestedAmounts,
    calculateTotalCarServiceExpenseReimbursementAmountWithOverride,
    calculateTotalMealsReimbursementAmountWithOverride,
    calculateTotalDependentCarExpenseReimbursementAmountWithOverride,
    calculateTotalMilesReimbursementAmountWithOverride,
    calculateRoundTripMiles
} from '../utils/approved-amounts.utils';

interface UseClaimVerificationProps {
    verifyTravel: (
        travelRequestId: number,
        roundTripMiles: number,
        hotelLodgingExpense: number,
        flightAndTrainExpense: number,
        carServiceExpense: number,
        dependentCareExpense: number,
        mealExpense: number,
        isMilesOverWritten: boolean,
        overrideMiles: number,
        isMealsOverWritten: boolean,
        overrideMeals: number,
        isCarServiceExpenseOverridden: boolean,
        overrideCarServiceExpense: number,
        isDependentCareExpenseOverridden: boolean,
        overrideDependentCareExpense: number,
        isHotelLodgingExpenseOverridden: boolean,
        overrideHotelLodgingExpense: number,
        approvalEmail: any
    ) => Promise<any>;
}

export const useClaimVerification = (props: UseClaimVerificationProps) => {
    const [isApprovingClaim, setIsApprovingClaim] = useState<boolean>(false);
    const [isDecliningClaim, setIsDecliningClaim] = useState<boolean>(false);
    const [claimApproved, setClaimApproved] = useState<boolean>(false);
    const [showApprovalEmailModal, setShowApprovalEmailModal] = useState<boolean>(false);
    const [approvalEmailSubject, setApprovalEmailSubject] = useState<string>('');
    const [approvalEmailContent, setApprovalEmailContent] = useState<string>('');
    const [approvalEmailFirstParagraph, setApprovalEmailFirstParagraph] = useState<string>('');

    const approveClaim = useCallback(async (
        travelRequestId: number,
        trips: IWatrTrip[],
        allLineItems: ILineItem[],
        overrideState: IOverrideState,
        maxNights?: number,
        limits?: IWatrLimit,
        reimbursementRate?: number
    ) => {
        setIsApprovingClaim(true);
        try {
            if (!limits) {
                throw new Error('Limits are required for claim approval');
            }

            const totalMiles = sumMileageRoundTripMiles(allLineItems);
            const expenseTotals = calculateExpenseTotals(allLineItems, maxNights);
            const storedRate = deriveMileageRateFromLineItems(allLineItems);

            const rate = storedRate > 0 ? storedRate : resolveMileageRate(reimbursementRate, undefined);
            const totalMileageReimbursement = sumMileageLineItemAmount(allLineItems);
            const rawRequestedAmounts = calculateRawRequestedAmounts(allLineItems, expenseTotals);
            const totalMilesReimbursementAmountWithOverride = calculateTotalMilesReimbursementAmountWithOverride(overrideState, totalMileageReimbursement, rate);
            const totalCarServiceExpenseReimbursementAmountWithOverride = calculateTotalCarServiceExpenseReimbursementAmountWithOverride(overrideState, rawRequestedAmounts, expenseTotals);
            const totalMealsReimbursementAmountWithOverride = calculateTotalMealsReimbursementAmountWithOverride(overrideState, rawRequestedAmounts, expenseTotals);
            const totalDependentCarExpenseReimbursementAmountWithOverride = calculateTotalDependentCarExpenseReimbursementAmountWithOverride(overrideState, rawRequestedAmounts, expenseTotals);

            const lodgingApproved = calculateApprovedLodgingAmount(allLineItems, limits, overrideState, 'overall');
            const flightApproved = calculateApprovedFlightAmount(allLineItems);
            const transportationApproved = calculateApprovedTransportationAmount(allLineItems, limits, overrideState, 'overall', totalCarServiceExpenseReimbursementAmountWithOverride);
            const mealsApproved = calculateApprovedMealsAmount(limits, overrideState, 'overall', totalMealsReimbursementAmountWithOverride);
            const dependentCareApproved = calculateApprovedDependentCareAmount(limits, overrideState, 'overall', totalDependentCarExpenseReimbursementAmountWithOverride);
            const roundTripMiles = calculateRoundTripMiles(overrideState, totalMiles, limits.mileage);
            
            const response = await props.verifyTravel(
                travelRequestId,
                roundTripMiles,
                lodgingApproved,
                flightApproved,
                transportationApproved,
                dependentCareApproved,
                mealsApproved,
                overrideState.isMilesOverWritten,
                overrideState.overrideMiles,
                overrideState.isMealsOverWritten,
                overrideState.overrideMeals,
                overrideState.isCarServiceExpenseOverridden,
                overrideState.overrideCarServiceExpense,
                overrideState.isDependentCareExpenseOverridden,
                overrideState.overrideDependentCareExpense,
                overrideState.isHotelLodgingExpenseOverridden,
                overrideState.overrideHotelLodgingExpense,
                null
            );
            
            if (response?.data?.success) {
                setClaimApproved(true);
                return { success: true };
            } else {
                const errorMessage = response?.data?.message || response?.data?.error || 'Failed to approve the travel claim. Please try again.';
                return { error: errorMessage };
            }
        } catch (err: any) {
            const errorMessage = err?.response?.data?.message || err?.message || 'An error occurred while approving the claim.';
            return { error: errorMessage };
        } finally {
            setIsApprovingClaim(false);
        }
    }, [props]);

    const approveClaimWithEmail = useCallback(async (
        travelRequestId: number,
        trips: IWatrTrip[],
        allLineItems: ILineItem[],
        overrideState: IOverrideState,
        maxNights?: number,
        limits?: IWatrLimit,
        reimbursementRate?: number
    ) => {
        if (!approvalEmailSubject || approvalEmailSubject.trim().length === 0) {
            return { error: 'Email Subject is required.' };
        }
        if (!approvalEmailContent || approvalEmailContent.trim().length === 0) {
            return { error: 'Email Content is required.' };
        }

        setIsApprovingClaim(true);
        try {
            if (!limits) {
                throw new Error('Limits are required for claim approval');
            }

            const totalMiles = sumMileageRoundTripMiles(allLineItems);
            const expenseTotals = calculateExpenseTotals(allLineItems, maxNights);
            const storedRate = deriveMileageRateFromLineItems(allLineItems);

            const rate = storedRate > 0 ? storedRate : resolveMileageRate(reimbursementRate, undefined);
            const totalMileageReimbursement = sumMileageLineItemAmount(allLineItems);
            const rawRequestedAmounts = calculateRawRequestedAmounts(allLineItems, expenseTotals);
            const totalMilesReimbursementAmountWithOverride = calculateTotalMilesReimbursementAmountWithOverride(overrideState, totalMileageReimbursement, rate);
            const totalCarServiceExpenseReimbursementAmountWithOverride = calculateTotalCarServiceExpenseReimbursementAmountWithOverride(overrideState, rawRequestedAmounts, expenseTotals);
            const totalMealsReimbursementAmountWithOverride = calculateTotalMealsReimbursementAmountWithOverride(overrideState, rawRequestedAmounts, expenseTotals);
            const totalDependentCarExpenseReimbursementAmountWithOverride = calculateTotalDependentCarExpenseReimbursementAmountWithOverride(overrideState, rawRequestedAmounts, expenseTotals);

            const lodgingApproved = calculateApprovedLodgingAmount(allLineItems, limits, overrideState, 'overall');
            const flightApproved = calculateApprovedFlightAmount(allLineItems);
            const transportationApproved = calculateApprovedTransportationAmount(allLineItems, limits, overrideState, 'overall', totalCarServiceExpenseReimbursementAmountWithOverride);
            const mealsApproved = calculateApprovedMealsAmount(limits, overrideState, 'overall', totalMealsReimbursementAmountWithOverride);
            const dependentCareApproved = calculateApprovedDependentCareAmount(limits, overrideState, 'overall', totalDependentCarExpenseReimbursementAmountWithOverride);
            const roundTripMiles = calculateRoundTripMiles(overrideState, totalMiles, limits.mileage);
            
            const approvalEmail = {
                emailSubject: approvalEmailSubject,
                firstParagraph: approvalEmailFirstParagraph,
                emailContent: approvalEmailContent.replace(/\n/g, '|'),
                lastParagraph: ''
            };
            
            const response = await props.verifyTravel(
                travelRequestId,
                roundTripMiles,
                lodgingApproved,
                flightApproved,
                transportationApproved,
                dependentCareApproved,
                mealsApproved,
                overrideState.isMilesOverWritten,
                overrideState.overrideMiles,
                overrideState.isMealsOverWritten,
                overrideState.overrideMeals,
                overrideState.isCarServiceExpenseOverridden,
                overrideState.overrideCarServiceExpense,
                overrideState.isDependentCareExpenseOverridden,
                overrideState.overrideDependentCareExpense,
                overrideState.isHotelLodgingExpenseOverridden,
                overrideState.overrideHotelLodgingExpense,
                approvalEmail
            );
            
            if (response?.data?.success) {
                setClaimApproved(true);
                setShowApprovalEmailModal(false);
                return { success: true };
            } else {
                return { error: 'Failed to approve the travel claim. Please try again.' };
            }
        } catch (err) {
            return { error: 'An error occurred while approving the claim.' };
        } finally {
            setIsApprovingClaim(false);
        }
    }, [props, approvalEmailSubject, approvalEmailContent, approvalEmailFirstParagraph]);

    return {
        isApprovingClaim,
        isDecliningClaim,
        claimApproved,
        showApprovalEmailModal,
        approvalEmailSubject,
        approvalEmailContent,
        approvalEmailFirstParagraph,
        approveClaim,
        approveClaimWithEmail,
        setShowApprovalEmailModal,
        setApprovalEmailSubject,
        setApprovalEmailContent,
        setApprovalEmailFirstParagraph,
        setIsDecliningClaim
    };
};

